// Ported from Dealtracker src/appframe/tours/rating.tsx (commit 30ca0cb).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

// The congratulations card offers "Rate the app", which opens the userhub widget's own Rate
// dialog. The widget does not export that dialog; its FeedbackWidget hands `onRate` to the host's
// renderTrigger (the account menu, TopBar.tsx), which registers it here and the tour engine reads
// it back. No widget mounted, or no onRate → `useOpenRating()` is null and the button hides.

interface RatingApi {
  open: (() => void) | null
  register: (fn: () => void) => () => void
}

const RatingContext = createContext<RatingApi>({ open: null, register: () => () => {} })

export function RatingProvider({ children }: { children?: ReactNode }) {
  // The latest callback in a ref (the widget re-creates it often); availability in state.
  const fnRef = useRef<(() => void) | null>(null)
  const [available, setAvailable] = useState(false)
  const register = useCallback((fn: () => void) => {
    fnRef.current = fn
    setAvailable(true)
    return () => {
      if (fnRef.current !== fn) return
      fnRef.current = null
      setAvailable(false)
    }
  }, [])
  const open = useCallback(() => fnRef.current?.(), [])
  const api = useMemo<RatingApi>(() => ({ open: available ? open : null, register }), [available, open, register])
  return <RatingContext.Provider value={api}>{children}</RatingContext.Provider>
}

/** Opens the userhub Rate dialog, or null when the widget offers none. */
export function useOpenRating(): (() => void) | null {
  return useContext(RatingContext).open
}

/** Rendered inside FeedbackWidget's renderTrigger: makes its `onRate` available to the tours. */
export function RegisterRating({ onRate }: { onRate?: () => void }) {
  const { register } = useContext(RatingContext)
  const latest = useRef(onRate)
  latest.current = onRate
  const has = !!onRate
  useEffect(() => (has ? register(() => latest.current?.()) : undefined), [has, register])
  return null
}
